import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useHistory } from 'react-router-dom';
import useUser from 'state/user';
import useWellness from 'state/wellness';
import SubpageTopBar from '../../components/SubpageTopBar';
import { nom035API } from 'resources/api';
import { IMAGES_URL } from 'resources/constants/urls';
import {
    BRAND_GREEN_PRIMARY,
    DARKER_TEXT,
    DARK_TEXT,
    ERROR_RED,
    WARNING_ORANGE,
    SUCCESS_GREEN,
} from 'resources/constants/colors';

// ---------------------------------------------------------------------------
// Invitaciones pendientes
// ---------------------------------------------------------------------------

const InviteBanner = styled.div`
    margin: 0 20px 8px;
    background: linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%);
    border: 1.5px solid #c8e6c9;
    border-radius: 16px;
    padding: 14px 16px;
    animation: ${keyframes`from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}`} 0.3s ease both;
`;

const InviteTitle = styled.p`
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    color: #388e3c;
    margin: 0 0 10px;
`;

const InviteCard = styled.div`
    background: #fff;
    border-radius: 12px;
    padding: 12px 14px;
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;
    box-shadow: 0 1px 4px rgba(0,0,0,0.07);
    &:last-child { margin-bottom: 0; }
`;

const InviteInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

const InviteName = styled.p`
    font-size: 14px;
    font-weight: 700;
    color: ${DARKER_TEXT};
    margin: 0 0 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const InviteLabel = styled.p`
    font-size: 12px;
    color: #66bb6a;
    font-weight: 600;
    margin: 0;
`;

const InviteActions = styled.div`
    display: flex;
    gap: 8px;
    flex-shrink: 0;
`;

const InviteBtn = styled.button`
    padding: 7px 14px;
    border-radius: 10px;
    border: none;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    transition: opacity 0.15s, transform 0.1s;
    background: ${({ $accept }) => $accept ? BRAND_GREEN_PRIMARY : '#f5f5f5'};
    color: ${({ $accept }) => $accept ? '#fff' : '#888'};
    &:active { opacity: 0.8; transform: scale(0.96); }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

// ---------------------------------------------------------------------------
// Animations
// ---------------------------------------------------------------------------

const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
    0%, 100% { box-shadow: 0 0 0 0 rgba(231,76,60,0.35); }
    50%       { box-shadow: 0 0 0 6px rgba(231,76,60,0); }
`;

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

const PageWrap = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 100%;
    padding-bottom: 90px;
    animation: ${fadeUp} 0.3s ease both;
`;



// ---------------------------------------------------------------------------
// Search bar
// ---------------------------------------------------------------------------

const SearchWrap = styled.div`
    padding: 0 20px 16px;
`;

const SearchInput = styled.input`
    width: 100%;
    background: #f5f5f5;
    border: 1.5px solid transparent;
    border-radius: 14px;
    padding: 11px 16px;
    font-size: 14px;
    color: ${DARKER_TEXT};
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.15s;
    &::placeholder { color: #bbb; }
    &:focus { border-color: ${BRAND_GREEN_PRIMARY}; background: #fff; }
`;

// ---------------------------------------------------------------------------
// Stats row
// ---------------------------------------------------------------------------

const StatsRow = styled.div`
    display: flex;
    gap: 10px;
    padding: 0 20px 20px;
`;

const StatCard = styled.div`
    flex: 1;
    background: ${({ accent }) => accent || '#fff'};
    border-radius: 14px;
    padding: 12px 14px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
`;

const StatNumber = styled.p`
    font-size: 24px;
    font-weight: 800;
    color: ${({ color }) => color || DARKER_TEXT};
    margin: 0 0 2px;
    line-height: 1;
`;

const StatLabel = styled.p`
    font-size: 11px;
    font-weight: 600;
    color: #999;
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

// ---------------------------------------------------------------------------
// Patient list
// ---------------------------------------------------------------------------

const ListWrap = styled.div`
    padding: 0 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const SectionLabel = styled.p`
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: #bbb;
    margin: 0 0 8px;
`;

const Card = styled.div`
    background: #fff;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 2px 10px rgba(0,0,0,0.06);
    animation: ${fadeUp} 0.3s ${({ index }) => index * 0.04}s ease both;
`;

const PatientRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    cursor: pointer;
    transition: background 0.12s;
    border-bottom: 1px solid #f5f5f5;
    &:last-child { border-bottom: none; }
    &:active { background: #fafafa; }
`;

const AvatarWrap = styled.div`
    position: relative;
    width: 46px;
    height: 46px;
    flex-shrink: 0;
    border-radius: 14px;
    overflow: hidden;
    img { width: 100%; height: 100%; object-fit: cover; }
`;

// Colores deterministas para avatares de iniciales
const AVATAR_COLORS = [
    ['#6C63FF', '#fff'], ['#26C6DA', '#fff'], ['#66BB6A', '#fff'],
    ['#FFA726', '#fff'], ['#EF5350', '#fff'], ['#AB47BC', '#fff'],
    ['#42A5F5', '#fff'], ['#8D6E63', '#fff'],
];

function getAvatarColor(name = '') {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

const InitialsAvatar = styled.div`
    width: 100%;
    height: 100%;
    background: ${({ bg }) => bg};
    color: ${({ fg }) => fg};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.5px;
    user-select: none;
`;

const StatusDot = styled.div`
    position: absolute;
    bottom: 1px;
    right: 1px;
    width: 11px;
    height: 11px;
    border-radius: 50%;
    border: 2px solid #fff;
    background: ${({ color }) => color};
    animation: ${({ $sos }) => $sos ? pulse : 'none'} 1.4s ease infinite;
`;

const PatientInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

const PatientName = styled.p`
    font-size: 14px;
    font-weight: 700;
    color: ${DARKER_TEXT};
    margin: 0 0 3px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const PatientMeta = styled.p`
    font-size: 12px;
    color: ${({ color }) => color || '#aaa'};
    font-weight: ${({ bold }) => bold ? '600' : '400'};
    margin: 0;
`;

const Arrow = styled.span`
    font-size: 20px;
    color: #d0d0d0;
    flex-shrink: 0;
`;

// NOM-035 risk badge
const NOM035_RISK_COLORS = {
    NULO:     { bg: '#e8f5e9', text: '#388e3c' },
    BAJO:     { bg: '#e8f5e9', text: '#388e3c' },
    MEDIO:    { bg: '#fff8e1', text: '#f57f17' },
    ALTO:     { bg: '#fff3e0', text: '#e65100' },
    MUY_ALTO: { bg: '#fce4ec', text: '#c62828' },
};

const Nom035Badge = styled.span`
    display: inline-block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.3px;
    padding: 2px 7px;
    border-radius: 6px;
    background: ${({ bg }) => bg};
    color: ${({ text }) => text};
    margin-top: 3px;
    text-transform: uppercase;
`;

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

const EmptyWrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 32px;
    text-align: center;
    gap: 10px;
`;

const EmptyIcon = styled.div`
    font-size: 52px;
    line-height: 1;
`;

const EmptyTitle = styled.p`
    font-size: 16px;
    font-weight: 700;
    color: ${DARK_TEXT};
    margin: 0;
`;

const EmptyBody = styled.p`
    font-size: 13px;
    color: #aaa;
    margin: 0;
    line-height: 1.5;
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const MisPacientes = ({ toggleMenu }) => {
    const [user, userDispatcher] = useUser();
    const [wellness] = useWellness();
    const history = useHistory();
    const [search, setSearch] = useState('');
    const [respondingUUID, setRespondingUUID] = useState(null);
    const [nom035Map, setNom035Map] = useState({});

    // Cargar estado NOM-035 de todos los pacientes
    useEffect(() => {
        nom035API.getPatientsAssessments()
            .then(data => {
                const map = {};
                (data || []).forEach(p => { map[p.patientId] = p; });
                setNom035Map(map);
            })
            .catch(() => {}); // no bloquear si falla
    }, []);

    const rawClients = useMemo(() => {
        const list = user.current?.extraData?.clients;
        return Array.isArray(list) ? list.filter(c => c && c.id && c.name) : [];
    }, [user]);

    // Invitaciones pendientes (accepted === null)
    const pendingInvitations = useMemo(() => {
        const list = user.current?.extraData?.invitations;
        return Array.isArray(list) ? list.filter(inv => inv.accepted === null) : [];
    }, [user]);

    const handleInviteResponse = (invitationUUID, accept) => {
        setRespondingUUID(invitationUUID);
        userDispatcher.acceptInvitationStart({ invitationUUID, accept });
    };

    const { sosAlerts = [], moodAlerts = [] } = wellness?.alerts ?? {};

    const sosCount   = useMemo(() => rawClients.filter(c => sosAlerts.some(a => a.clientId === c.id)).length, [rawClients, sosAlerts]);
    const moodCount  = useMemo(() => rawClients.filter(c => moodAlerts.some(a => a.clientId === c.id)).length, [rawClients, moodAlerts]);
    const stableCount = rawClients.length - sosCount - moodCount;

    const filtered = useMemo(() => {
        if (!search.trim()) return rawClients;
        const q = search.toLowerCase().trim();
        return rawClients.filter(c =>
            `${c.name} ${c.lastName}`.toLowerCase().includes(q)
        );
    }, [rawClients, search]);

    // Ordenar: SOS primero → mood alert → resto
    const sorted = useMemo(() => [...filtered].sort((a, b) => {
        const aSos  = sosAlerts.some(x => x.clientId === a.id) ? 2 : 0;
        const bSos  = sosAlerts.some(x => x.clientId === b.id) ? 2 : 0;
        const aMood = moodAlerts.some(x => x.clientId === a.id) ? 1 : 0;
        const bMood = moodAlerts.some(x => x.clientId === b.id) ? 1 : 0;
        return (bSos + bMood) - (aSos + aMood);
    }), [filtered, sosAlerts, moodAlerts]);

    return (
        <PageWrap>
            <SubpageTopBar toggleMenu={toggleMenu} title="Mis pacientes" />

            {rawClients.length > 0 && (
                <>
                    {/* Stats */}
                    <StatsRow>
                        <StatCard accent="#fff9f9">
                            <StatNumber color={sosCount > 0 ? ERROR_RED : '#ccc'}>{sosCount}</StatNumber>
                            <StatLabel>SOS activos</StatLabel>
                        </StatCard>
                        <StatCard accent="#fffbf5">
                            <StatNumber color={moodCount > 0 ? WARNING_ORANGE : '#ccc'}>{moodCount}</StatNumber>
                            <StatLabel>Mood bajo</StatLabel>
                        </StatCard>
                        <StatCard accent="#f5fff7">
                            <StatNumber color={SUCCESS_GREEN}>{stableCount}</StatNumber>
                            <StatLabel>Estables</StatLabel>
                        </StatCard>
                    </StatsRow>

                    {/* Search */}
                    <SearchWrap>
                        <SearchInput
                            type="text"
                            placeholder="Buscar paciente..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </SearchWrap>
                </>
            )}

            {/* Invitaciones pendientes del terapeuta */}
            {pendingInvitations.length > 0 && (
                <InviteBanner>
                    <InviteTitle>🔔 Solicitudes de asignación</InviteTitle>
                    {pendingInvitations.map(inv => {
                        const name = inv.otherUserName
                            ? `${inv.otherUserName} ${inv.otherUserLastName ?? ''}`.trim()
                            : 'Paciente';
                        const [bg, fg] = getAvatarColor(name);
                        const initials = `${inv.otherUserName?.[0] ?? ''}${inv.otherUserLastName?.[0] ?? ''}`.toUpperCase();
                        const isLoading = respondingUUID === inv.invitationUUID;
                        return (
                            <InviteCard key={inv.invitationUUID}>
                                <AvatarWrap style={{ width: 42, height: 42, flexShrink: 0 }}>
                                    {inv.otherUserProfileImg
                                        ? <img src={`${IMAGES_URL}${inv.otherUserProfileImg}`} alt={name} />
                                        : <InitialsAvatar bg={bg} fg={fg}>{initials}</InitialsAvatar>
                                    }
                                </AvatarWrap>
                                <InviteInfo>
                                    <InviteName>{name}</InviteName>
                                    <InviteLabel>Quiere que seas su terapeuta</InviteLabel>
                                </InviteInfo>
                                <InviteActions>
                                    <InviteBtn
                                        disabled={isLoading}
                                        onClick={() => handleInviteResponse(inv.invitationUUID, false)}
                                    >
                                        Rechazar
                                    </InviteBtn>
                                    <InviteBtn
                                        $accept
                                        disabled={isLoading}
                                        onClick={() => handleInviteResponse(inv.invitationUUID, true)}
                                    >
                                        Aceptar
                                    </InviteBtn>
                                </InviteActions>
                            </InviteCard>
                        );
                    })}
                </InviteBanner>
            )}

            <ListWrap>
                {sorted.length === 0 && rawClients.length > 0 ? (
                    <EmptyWrap>
                        <EmptyIcon>🔍</EmptyIcon>
                        <EmptyTitle>Sin resultados</EmptyTitle>
                        <EmptyBody>No hay pacientes que coincidan con "{search}"</EmptyBody>
                    </EmptyWrap>
                ) : sorted.length === 0 ? (
                    <EmptyWrap>
                        <EmptyIcon>👥</EmptyIcon>
                        <EmptyTitle>Sin pacientes aún</EmptyTitle>
                        <EmptyBody>
                            Cuando un paciente te seleccione como terapeuta, aparecerá aquí para hacer seguimiento.
                        </EmptyBody>
                    </EmptyWrap>
                ) : (
                    <>
                        {sosCount > 0 && <SectionLabel>⚠️ Requieren atención</SectionLabel>}
                        <Card>
                            {sorted.map((person, index) => {
                                const hasSos  = sosAlerts.some(a => a.clientId === person.id);
                                const hasMood = moodAlerts.some(a => a.clientId === person.id);
                                const statusColor = hasSos ? ERROR_RED : hasMood ? WARNING_ORANGE : SUCCESS_GREEN;
                                const statusLabel = hasSos ? '🆘 SOS reciente' : hasMood ? '📉 Estado de ánimo bajo' : null;
                                const [bg, fg] = getAvatarColor(`${person.name}${person.lastName}`);
                                const initials = `${person.name?.[0] ?? ''}${person.lastName?.[0] ?? ''}`.toUpperCase();

                                const nom035 = nom035Map[person.id];
                                const riskColors = nom035?.riskLevel ? NOM035_RISK_COLORS[nom035.riskLevel] : null;
                                const riskLabel = nom035?.riskLevel
                                    ? nom035.riskLevel.replace('_', ' ')
                                    : nom035 === undefined ? null : 'SIN EVALUACIÓN';

                                return (
                                    <PatientRow
                                        key={person.id}
                                        index={index}
                                        onClick={() => history.push(`/home/expediente/${person.id}`)}
                                    >
                                        <AvatarWrap>
                                            {person.profileImg
                                                ? <img src={`${IMAGES_URL}${person.profileImg}`} alt={person.name} />
                                                : <InitialsAvatar bg={bg} fg={fg}>{initials}</InitialsAvatar>
                                            }
                                            <StatusDot color={statusColor} $sos={hasSos ? 1 : 0} />
                                        </AvatarWrap>
                                        <PatientInfo>
                                            <PatientName>{person.name} {person.lastName}</PatientName>
                                            {statusLabel
                                                ? <PatientMeta color={statusColor} bold>{statusLabel}</PatientMeta>
                                                : <PatientMeta>Sin alertas activas</PatientMeta>
                                            }
                                            {nom035 && riskLabel && (
                                                <Nom035Badge
                                                    bg={riskColors ? riskColors.bg : '#f5f5f5'}
                                                    text={riskColors ? riskColors.text : '#999'}
                                                >
                                                    NOM-035: {riskLabel}
                                                </Nom035Badge>
                                            )}
                                        </PatientInfo>
                                        <Arrow>›</Arrow>
                                    </PatientRow>
                                );
                            })}
                        </Card>
                    </>
                )}
            </ListWrap>
        </PageWrap>
    );
};

export default MisPacientes;
